/**
 * Modal dialog — generate / modify / void confirmations (design spec §2).
 *
 * Focus is trapped while it is open, Escape closes it, and the backdrop click
 * closes only when the dialog is not busy: a confirmation that has already been
 * submitted must not vanish under a stray click while the request is in flight.
 */

import { useCallback, useEffect, useId, useRef, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { IconButton } from './primitives.tsx'

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

export function Dialog({
  open,
  title,
  subtitle,
  onClose,
  children,
  footer,
  wide,
  busy,
}: {
  open: boolean
  title: ReactNode
  /** A second line under the title — the guarantee a dialog makes up front. */
  subtitle?: ReactNode
  onClose: () => void
  children: ReactNode
  footer?: ReactNode
  wide?: boolean
  busy?: boolean
}) {
  const { t } = useTranslation()
  const titleId = useId()
  const dialogRef = useRef<HTMLDivElement>(null)
  const bodyRef = useRef<HTMLDivElement>(null)
  const restoreFocusTo = useRef<Element | null>(null)

  const requestClose = useCallback(() => {
    if (!busy) onClose()
  }, [busy, onClose])

  /**
   * The latest `requestClose`, read by the key handler below.
   *
   * Not a dependency of the effect, and that is the whole fix for #59. `onClose` is an inline
   * arrow at every call site, so `requestClose` gets a new identity on every render; with it in
   * the dependency list the effect tore down and re-ran on **every keystroke**, re-running its
   * focus line and taking focus out of the field the user was typing in. The effect now runs when
   * the dialog opens and closes, which is when it has anything to do.
   */
  const requestCloseRef = useRef(requestClose)
  useEffect(() => {
    requestCloseRef.current = requestClose
  })

  useEffect(() => {
    if (!open) return
    restoreFocusTo.current = document.activeElement
    const node = dialogRef.current
    /**
     * The body first, the dialog only as a fallback.
     *
     * "First focusable in the dialog" is the ✕: the header is rendered before the body. So a
     * dialog whose body has a field opens on its close button — Enter dismisses what the user came
     * to fill in. A dialog whose body is only text (the void and generate confirmations) still
     * falls through to the ✕, which is the right default there.
     */
    const target =
      bodyRef.current?.querySelector<HTMLElement>(FOCUSABLE) ?? node?.querySelector<HTMLElement>(FOCUSABLE)
    target?.focus()

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.stopPropagation()
        requestCloseRef.current()
        return
      }
      if (event.key !== 'Tab' || !node) return
      const focusable = [...node.querySelectorAll<HTMLElement>(FOCUSABLE)]
      if (focusable.length === 0) return
      const first = focusable[0]!
      const last = focusable[focusable.length - 1]!
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown, true)
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKeyDown, true)
      document.body.style.overflow = previousOverflow
      if (restoreFocusTo.current instanceof HTMLElement) restoreFocusTo.current.focus()
    }
  }, [open])

  if (!open) return null

  return (
    <div
      className="dialog-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) requestClose()
      }}
    >
      {/* Named by its title: without it a screen reader announces "dialog" and nothing of what it is for. */}
      <div
        className={wide ? 'dialog dialog--wide' : 'dialog'}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={dialogRef}
      >
        <header className="dialog__header">
          <div>
            <h2 id={titleId} className="dialog__title">
              {title}
            </h2>
            {subtitle ? <p className="small muted">{subtitle}</p> : null}
          </div>
          <IconButton icon="x" label={t('common.close')} onClick={requestClose} disabled={busy} />
        </header>
        <div className="dialog__body" ref={bodyRef}>
          {children}
        </div>
        {footer ? <footer className="dialog__footer">{footer}</footer> : null}
      </div>
    </div>
  )
}
